/**
 * Named avatar size tokens, in pixels, used everywhere an actor avatar
 * is rendered. Each token corresponds to a specific surface so that
 * sizing stays consistent across the product:
 *
 *   - `profile`   (64px / size-16): the large avatar on a user or
 *                                    Partea profile plate.
 *   - `inboxRow`  (48px / size-12): the chat conversation list row.
 *   - `header`    (36px / size-9):  the chat conversation header and
 *                                    search result rows.
 *   - `message`   (32px / size-8):  in-thread chat message bubbles,
 *                                    comment author avatars, the
 *                                    AppBar trigger, and most generic
 *                                    actor chips.
 *   - `rail`      (56px / size-14): the Instagram-style story rail at
 *                                    the top of the home feed.
 *   - `chip`      (24px / size-6):  ultra-compact actor mini-avatars
 *                                    (e.g. inside the entity actor row).
 *
 * Always import these tokens instead of hardcoding pixel sizes — that
 * way a future product tweak (e.g. "make every avatar 4px bigger on
 * mobile") is a single-file change.
 */
export const ACTOR_AVATAR_SIZES = {
  profile: 64,
  inboxRow: 48,
  header: 36,
  message: 32,
  rail: 56,
  chip: 24,
  mini: 16,
} as const;

export type ActorAvatarSizeName = keyof typeof ACTOR_AVATAR_SIZES;

/**
 * Pixel value for a named token, or the value itself when callers want
 * a one-off size. Keeps the type signature of `<ActorAvatar size={...}>`
 * narrow without forcing every caller through the token map.
 */
export const resolveActorAvatarSize = (
  size: number | ActorAvatarSizeName
): number => {
  if (typeof size === "number") {
    return size;
  }
  return ACTOR_AVATAR_SIZES[size];
};
